import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useLingui } from "@lingui/react/macro";
import { ChevronRight } from "lucide-react";

import { cn, localize } from "@ando/ui";

import type { CatalogEntry } from "../catalog";
import { CapabilityBadge } from "./CapabilityBadge";

/**
 * CapabilityRow — single horizontal row primitive shared by every
 * variant on the integrations hub (connected / disconnected / errored /
 * reauth-needed). Previously 4 parallel JSX shells diverged silently
 * (different padding, different border tokens, different chevron
 * styles). Single primitive = single source of truth for the row chrome.
 *
 * Variants by ``tone``:
 *   - "default"  — neutral (disconnected, "Da connettere")
 *   - "success"  — green border (connected, healthy)
 *   - "warning"  — amber border + bg tint (needs_reauth, errored load)
 *
 * The ``leading`` slot accepts a custom badge override for cases where
 * the caller wants a muted/colored variant; defaults to the brand-aware
 * ``<CapabilityBadge>`` if not provided.
 */
export interface CapabilityRowProps {
  readonly entry: CatalogEntry;
  readonly primaryVendor?: string | null;
  readonly tone?: "default" | "success" | "warning";
  /** Variant override for the leading badge slot. */
  readonly leading?: ReactNode;
  /** Truncated row title — defaults to ``entry.spec.title``. */
  readonly title?: string;
  /** Secondary line under the title (status, last-sync, error msg…). */
  readonly subtitle?: ReactNode;
  /** Optional inline action button before the chevron. */
  readonly action?: ReactNode;
  /** Render as a ``<Link>`` to the capability route (default) or a
   * ``<button>`` controlled by the parent. */
  readonly mode?: "link" | "button";
  readonly onClick?: () => void;
}

const TONE_CLASSES: Readonly<Record<"default" | "success" | "warning", string>> = {
  default: "border-hairline bg-card/60 hover:bg-card/85",
  success: "border-hairline bg-card/60 hover:bg-card/85",
  warning: "border-warning/40 bg-warning/[0.04] hover:bg-warning/[0.06]",
};

const FOCUS_RING: Readonly<Record<"default" | "success" | "warning", string>> = {
  default: "focus-visible:ring-ando-orange",
  success: "focus-visible:ring-ando-orange",
  warning: "focus-visible:ring-warning",
};

export const CapabilityRow: React.FC<CapabilityRowProps> = ({
  entry,
  primaryVendor,
  tone = "default",
  leading,
  title,
  subtitle,
  action,
  mode = "link",
  onClick,
}) => {
  const { i18n } = useLingui();
  const inner = (
    <>
      {leading ?? <CapabilityBadge entry={entry} primaryVendor={primaryVendor} />}
      <div className="flex min-w-0 flex-1 flex-col">
        <span className="truncate font-medium text-ink-1 text-sm">
          {title ?? localize(entry.spec.title, i18n)}
        </span>
        {subtitle ? (
          <span className="flex items-center gap-1.5 truncate text-[11px] text-ink-3">
            {subtitle}
          </span>
        ) : null}
      </div>
      {/* Action + chevron read as ONE affordance ("Gestisci ›"): grouped
          tight (gap-1), centered, and orange on hover like the "Collega ›"
          cards — the shared interactive cue. The chevron inherits the
          cluster colour (no own text token). */}
      <div className="flex shrink-0 items-center gap-1 text-ink-2 transition-colors group-hover:text-ando-orange">
        {action}
        <ChevronRight
          size={14}
          aria-hidden
          className="transition-transform group-hover:translate-x-0.5"
        />
      </div>
    </>
  );

  const baseCls = cn(
    "group flex w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2",
    TONE_CLASSES[tone],
    FOCUS_RING[tone],
  );

  if (mode === "button") {
    return (
      <button type="button" onClick={onClick} className={baseCls}>
        {inner}
      </button>
    );
  }

  return (
    <Link to="/integrations/$capability" params={{ capability: entry.slug }} className={baseCls}>
      {inner}
    </Link>
  );
};
CapabilityRow.displayName = "CapabilityRow";
